import { EVENT_GUEST_ATTENDING_WITH_OBJECT } from "@anggunpraya-templates/base/app/constant";
import { useEventGuestStore } from "../stores/event-guest";
import { useTemplateStore } from "../stores/template";
import { usePackageCapabilities } from "./use-package-capabilities";

/**
 * Composable shared untuk business flow opening card.
 *
 * Komponen UI tetap hidup di template, tetapi:
 * - nama pasangan
 * - nama tamu
 * - policy `openInvitation`
 * disamakan di layer base agar tidak drift antar template.
 *
 * @returns Derived state dan action yang dibutuhkan komponen opening card
 *
 * @example
 * ```typescript
 * const { guestText, openInvitation } = useOpeningCard();
 * ```
 */
export function useOpeningCard() {
  const { $services } = useNuxtApp();
  const { flavor } = useRuntimeConfig().public;

  const { coupleName } = storeToRefs(useTemplateStore());
  const { eventGuest } = storeToRefs(useEventGuestStore());
  const { supportsOpenTracking } = usePackageCapabilities();

  const { isEventPreview, isGuestBased, recipientName } = useInvitationContext();

  /** Nama tamu yang sudah diformat sesuai nilai `attending_with`. */
  const guestText = computed(() => {
    if (!isGuestBased.value) {
      return recipientName.value || "Tamu Undangan";
    }

    if (!eventGuest.value) {
      return "";
    }

    const { name, attending_with } = eventGuest.value;

    if (attending_with === "alone") {
      return name;
    }

    return `${name} & ${EVENT_GUEST_ATTENDING_WITH_OBJECT[attending_with]}`;
  });

  /**
   * Menjalankan side-effect "open invitation" hanya untuk flavor event nyata.
   *
   * Return `true` bila UI boleh lanjut membuka cover,
   * dan `false` bila flow harus berhenti karena data tamu tidak valid
   * atau request gagal.
   *
   * @returns Boolean yang menandakan apakah UI boleh lanjut membuka undangan
   */
  async function openInvitation() {
    if (flavor !== "event" || isEventPreview.value || !supportsOpenTracking.value) {
      return true;
    }

    if (!eventGuest.value) {
      showError({
        fatal: true,
        statusCode: 400,
        message: "Data tamu undangan tidak ditemukan.",
      });

      return false;
    }

    try {
      $services.event.openInvitation(eventGuest.value.event_id, eventGuest.value.event_guest_id);

      return true;
    }
    catch (error) {
      if (error instanceof Error) {
        const message = error.message || "Terjadi kesalahan saat membuka undangan.";
        console.error(message);
      }

      return false;
    }
  }

  return {
    coupleName,
    guestText,
    openInvitation,
  };
}
